interface IDateFormat {
  date?: string;
  locale?: locales;
  format?: formats;
  timeZone?: string;
}

export type DateType = 'date' | 'time';
export interface IDateConfigObject {
  options: Intl.DateTimeFormatOptions;
  type: DateType;
  transform?: (str: string) => string;
}
interface ITiming {
  readonly timeMilliseconds?: string;
  readonly timeSeconds?: string;
  readonly timeHours?: string;
  readonly timeMinutes?: string;
}

export interface IDateConfigMap {
  [key: string]: IDateConfigObject;
}

export const TYPE_DATE: DateType = 'date';
export const TYPE_TIME: DateType = 'time';

type locales = 'it' | 'it-IT';

export type formats =
  | 'DD/MM/YYYY'
  | 'ddd D MMM'
  | 'dddd D MMM YYYY'
  | 'ddd D MMM YYYY'
  | 'dddd D MMMM YYYY'
  | 'dddd D MMMM'
  | 'dddd D MMMM YYYY hh:mm'
  | 'DD MMM YYYY hh:mm'
  | 'DD MMMM YYYY'
  | 'DD MMMM YYYY hh:mm'
  | 'DD/MM, hh:mm'
  | 'D MMMM'
  | 'D MMM'
  | 'D'
  | 'hh:mm:ss'
  | 'hh:mm'
  | 'HH:mm';

export const formatsList: Array<formats> = [
  'DD/MM/YYYY',
  'ddd D MMM',
  'dddd D MMM YYYY',
  'ddd D MMM YYYY',
  'dddd D MMMM YYYY',
  'dddd D MMMM',
  'dddd D MMMM YYYY hh:mm',
  'DD MMM YYYY hh:mm',
  'DD MMMM YYYY',
  'DD MMMM YYYY hh:mm',
  'DD/MM, hh:mm',
  'D MMMM',
  'D MMM',
  'D',
  'hh:mm:ss',
  'hh:mm',
  'HH:mm',
];

export const getDateConfigMap = (): IDateConfigMap => {
  return {
    /** DATE TYPES */
    'DD/MM/YYYY': {
      options: { year: 'numeric', month: '2-digit', day: '2-digit' },
      type: TYPE_DATE,
    },
    'ddd D MMM': {
      options: { weekday: 'short', month: 'short', day: 'numeric' },
      type: TYPE_DATE,
    },
    'ddd D MMM YYYY': {
      options: { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' },
      type: TYPE_DATE,
    },
    'dddd D MMMM YYYY': {
      options: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
      type: TYPE_DATE,
    },
    'dddd D MMMM YYYY hh:mm': {
      options: {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      },
      type: TYPE_DATE,
    },
    'dddd D MMMM': {
      options: { weekday: 'long', month: 'long', day: 'numeric' },
      type: TYPE_DATE,
    },
    'dddd D MMM YYYY': {
      options: { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' },
      type: TYPE_DATE,
    },
    'DD MMM YYYY hh:mm': {
      options: { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: 'numeric' },
      type: TYPE_DATE,
    },
    'DD MMMM YYYY': {
      options: { day: 'numeric', month: 'long', year: 'numeric' },
      type: TYPE_DATE,
    },
    'DD MMMM YYYY hh:mm': {
      options: { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: 'numeric' },
      type: TYPE_DATE,
    },
    'DD/MM, hh:mm': {
      options: { day: 'numeric', month: '2-digit', hour: 'numeric', minute: 'numeric' },
      type: TYPE_DATE,
    },
    'D MMMM': { options: { day: 'numeric', month: 'long' }, type: TYPE_DATE },
    'D MMM': { options: { day: 'numeric', month: 'short' }, type: TYPE_DATE },
    D: { options: { day: 'numeric' }, type: TYPE_DATE },

    /** TIME TYPES */
    'hh:mm:ss': { options: { hour: '2-digit', minute: '2-digit', second: '2-digit' }, type: TYPE_TIME },
    'hh:mm': { options: { hour: '2-digit', minute: '2-digit' }, type: TYPE_TIME },
    'HH:mm': { options: { hour: '2-digit', minute: '2-digit' }, type: TYPE_TIME },
  };
};

/** DateForma function - it works both in CSR and SSR */
export const dateFormat = ({
  date,
  locale = 'it-IT',
  format = 'dddd D MMMM YYYY',
  timeZone = 'Europe/Rome',
}: IDateFormat): string => {
  // It's difficult to mock NEW DATE() in TS without a library like MOCKDATE https://www.npmjs.com/package/mockdate
  // So I added DATE.NOW(), easier to mock. See https://codewithhugo.com/mocking-the-current-date-in-jest-tests/
  const dateObject = date ? new Date(date) : new Date(Date.now());
  let dateElm;
  const { options, type, transform } = getDateConfigMap()[format];
  /* istanbul ignore next: else statement is unreachable */ if (type === TYPE_TIME) {
    dateElm = dateObject?.toLocaleTimeString(locale, { ...options, timeZone });
  } else if (type === TYPE_DATE) {
    dateElm = dateObject?.toLocaleDateString(locale, { ...options, timeZone });
  } else {
    return date || '';
  }

  const transformed = transform && dateElm ? transform(dateElm) : dateElm;
  return format.includes(',') ? transformed : transformed.replace(/, /g, ' '); // Removes ", " if NOT present in passed FORMAT
};

/* istanbul ignore next: private function */
const padStartTiming = (value: string, maxLength = 2, fillString = '0') => value.padStart(maxLength, fillString);

/**
 * Format timing element
 * @param timing the timing object
 * @returns formatted timings string
 */
export const formatTiming = (timing: ITiming | undefined): string => {
  if (typeof timing === 'undefined') {
    return '';
  }
  const { timeHours, timeMinutes = '00', timeSeconds = '00', timeMilliseconds = '000' } = timing;
  const hoursFormatted = Number(timeHours) ? `${timeHours}:` : '';
  const minutesFormatted = Number(timeHours) ? padStartTiming(timeMinutes) : timeMinutes;
  const secondsFormatted = padStartTiming(timeSeconds);
  const millisecondsFormatted = padStartTiming(timeMilliseconds, 3);
  return `${hoursFormatted}${minutesFormatted}:${secondsFormatted}.${millisecondsFormatted}`;
};

/** UTILS on dateString */
/** Delete comma in dateString */
const removeComma = (dateString: string) => dateString.replace(/,/g, '');

/** Uppercase Datestring */
const upperDate = (dateString: string) =>
  dateString.replace(/(^\w{1})|(\s+\w{1})/g, (letter: string) => letter.toUpperCase());

/** Remove comma in dates */
export const transformDateString = (dateString: string) => upperDate(removeComma(dateString));

export const uniqueId = (length: number = 16): number => {
  return parseInt(
    Math.ceil(Math.random() * Date.now())
      .toPrecision(length)
      .toString()
      .replace('.', ''),
    10
  );
};

export const getNowPlusOneHour = () => new Date(Date.now() + 3_600_000).toISOString();

export const setMatchDate = () => new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString();

/**
 * @param {string} isoTimestamp - isoTimestamp string
 * @returns {boolean} - Boolean response
 */
export const isDateValid = (isoTimestamp: string) => {
  const parsedIsoTimestamp = Date.parse(isoTimestamp);
  return !isNaN(parsedIsoTimestamp);
};
